import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DollarSign, Save, AlertTriangle, AlertCircle, TrendingUp, TrendingDown, Wallet, PiggyBank, Percent, Target, Calendar, Users, BarChart3, XCircle, Calculator, ChevronDown, ChevronUp } from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchProgramBudget,
  updateProgramBudget,
  selectProgramBudget,
  selectCostsLoading,
  selectCostsError,
  clearError,
} from '@/store/slices/costsSlice';
import { LoadingSpinner } from '@/components/ui';
import {
  GlassCard,
  GlassButton,
  MetricCard,
  ProgressBar,
} from '@/design-system';
import { UserRole } from '@/types';
import { formatCurrencyCompact, formatCurrencyFull, formatPercent } from '@/utils/formatters';

interface ProgramBudgetTabProps {
  programId: string;
  programName: string;
}

const ProgramBudgetTab: React.FC<ProgramBudgetTabProps> = ({
  programId,
  programName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const budget = useSelector(selectProgramBudget);
  const loading = useSelector(selectCostsLoading);
  const error = useSelector(selectCostsError);
  const { user } = useSelector((state: RootState) => state.auth);

  // Form state
  const [formData, setFormData] = useState({
    annualBudget: '',
    operationalOverhead: '',
    laborBudget: '',
    nonLaborBudget: '',
    budgetFiscalYear: new Date().getFullYear().toString(),
  });
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showTeamBreakdown, setShowTeamBreakdown] = useState(false);

  // Role-based access
  const canEdit = user?.role === UserRole.ADMIN || user?.role === UserRole.FINANCE || user?.role === UserRole.PROGRAM_LEAD;

  useEffect(() => {
    if (programId) {
      dispatch(fetchProgramBudget(programId));
    }
  }, [dispatch, programId]);

  useEffect(() => {
    if (budget && budget.budget) {
      setFormData({
        annualBudget: budget.budget.annual?.toString() || '',
        operationalOverhead: budget.budget.operationalOverheadPercent?.toString() || '20',
        laborBudget: budget.budget.labor?.toString() || '',
        nonLaborBudget: budget.budget.nonLabor?.toString() || '',
        budgetFiscalYear: new Date().getFullYear().toString(),
      });
      setIsDirty(false);
    }
  }, [budget]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [error, dispatch]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    const data: any = {};
    if (formData.annualBudget) data.annualBudget = parseFloat(formData.annualBudget);
    if (formData.operationalOverhead) data.operationalOverhead = parseFloat(formData.operationalOverhead) / 100;
    if (formData.laborBudget) data.laborBudget = parseFloat(formData.laborBudget);
    if (formData.nonLaborBudget) data.nonLaborBudget = parseFloat(formData.nonLaborBudget);
    if (formData.budgetFiscalYear) data.budgetFiscalYear = parseInt(formData.budgetFiscalYear);

    const result = await dispatch(updateProgramBudget({ programId, data }));
    if (updateProgramBudget.fulfilled.match(result)) {
      setIsDirty(false);
      setSaveSuccess(true);
      // Refetch full budget data as a safety net to ensure UI consistency
      // The PUT response should already contain the full data, but this ensures
      // data consistency even if the response structure changes
      dispatch(fetchProgramBudget(programId));
      setTimeout(() => setSaveSuccess(false), 3000);
    }
  };

  if (loading.budget) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const utilizationPercent = budget?.utilizationPercent ?? 0;
  const utilizationColor = utilizationPercent > 100 ? 'rose' : utilizationPercent > 80 ? 'amber' : 'emerald';

  return (
    <div className="space-y-4 md:space-y-5 lg:space-y-6">
      {/* Error Display */}
      {error && (
        <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-xl">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-status-error" />
            <p className="text-status-error text-sm font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {saveSuccess && (
        <div className="p-4 bg-status-success/10 border border-status-success/30 rounded-xl">
          <p className="text-status-success text-sm font-medium">Budget saved successfully!</p>
        </div>
      )}

      {/* Over-Budget Warning Alert */}
      {budget?.budget?.annual && (budget.committed?.committedPercent ?? 0) > 100 && (
        <div className="p-4 bg-status-error-bg border-2 border-status-error/40 rounded-xl shadow-lg animate-pulse-slow">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-2 bg-status-error/20 rounded-full">
              <XCircle className="w-6 h-6 text-status-error" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-status-error mb-1">
                Budget Over-Committed
              </h4>
              <p className="text-sm text-status-error mb-2">
                Allocations exceed the annual budget by{' '}
                <span className="font-bold">
                  {formatCurrencyCompact((budget.committed?.totalCost ?? 0) - (budget.budget?.annual ?? 0))}
                </span>{' '}
                ({formatPercent((budget.committed?.committedPercent ?? 0) - 100)} over budget).
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-status-error/70">Budget:</span>
                  <span className="font-semibold text-status-error">{formatCurrencyCompact(budget.budget?.annual)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-status-error/70">Committed:</span>
                  <span className="font-semibold text-status-error">{formatCurrencyCompact(budget.committed?.totalCost ?? 0)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-status-error/70">Excess:</span>
                  <span className="font-bold text-status-error">
                    {formatCurrencyCompact((budget.committed?.totalCost ?? 0) - (budget.budget?.annual ?? 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* At-Risk Warning Alert (80-100% committed) */}
      {budget?.budget?.annual && (budget.committed?.committedPercent ?? 0) > 80 && (budget.committed?.committedPercent ?? 0) <= 100 && (
        <div className="p-4 bg-status-warning-bg border-2 border-status-warning/40 rounded-xl">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-2 bg-status-warning/20 rounded-full">
              <AlertCircle className="w-6 h-6 text-status-warning" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-status-warning mb-1">
                Budget At Risk
              </h4>
              <p className="text-sm text-status-warning mb-2">
                Allocations are at{' '}
                <span className="font-bold">{formatPercent(budget.committed?.committedPercent ?? 0)}</span>{' '}
                of the annual budget. Only{' '}
                <span className="font-bold">{formatCurrencyCompact(budget.committed?.budgetAfterCommitted ?? 0)}</span>{' '}
                remains available for new commitments.
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-status-warning/70">Budget:</span>
                  <span className="font-semibold text-status-warning">{formatCurrencyCompact(budget.budget?.annual)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-status-warning/70">Committed:</span>
                  <span className="font-semibold text-status-warning">{formatCurrencyCompact(budget.committed?.totalCost ?? 0)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-status-warning/70">Remaining:</span>
                  <span className="font-bold text-status-warning">
                    {formatCurrencyCompact(budget.committed?.budgetAfterCommitted ?? 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Over-Spent Warning Alert */}
      {budget?.budget?.annual && utilizationPercent > 100 && (
        <div className="p-4 bg-status-error-bg border-2 border-status-error/40 rounded-xl shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-2 bg-status-error/20 rounded-full">
              <AlertTriangle className="w-6 h-6 text-status-error" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-status-error mb-1">
                Budget Over-Spent
              </h4>
              <p className="text-sm text-status-error mb-2">
                Actual spending has exceeded the annual budget by{' '}
                <span className="font-bold">
                  {formatCurrencyCompact((budget.actuals?.totalCost ?? 0) - (budget.budget?.annual ?? 0))}
                </span>{' '}
                ({formatPercent(utilizationPercent - 100)} over budget).
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-status-error/70">Budget:</span>
                  <span className="font-semibold text-status-error">{formatCurrencyCompact(budget.budget?.annual)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-status-error/70">Spent:</span>
                  <span className="font-semibold text-status-error">{formatCurrencyCompact(budget.actuals?.totalCost)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-status-error/70">Overspent:</span>
                  <span className="font-bold text-status-error">
                    {formatCurrencyCompact((budget.actuals?.totalCost ?? 0) - (budget.budget?.annual ?? 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Cost Exceeds Labor Budget Warning */}
      {budget?.variance?.status === 'OVER_BUDGET' && (
        <div className="p-4 bg-orange-500/10 border-2 border-orange-500/40 rounded-xl shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 p-2 bg-orange-500/20 rounded-full">
              <Calculator className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-base font-bold text-orange-700 dark:text-orange-300 mb-1">
                Team Cost Exceeds Labor Budget
              </h4>
              <p className="text-sm text-orange-600 dark:text-orange-400 mb-2">
                Your teams cost{' '}
                <span className="font-bold">
                  {formatCurrencyCompact(Math.abs(budget.variance.laborBudgetVsCalculated))}
                </span>{' '}
                more than the labor budget ({formatPercent(Math.abs(budget.variance.laborBudgetVsCalculatedPercent))} over).
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="text-orange-600/70 dark:text-orange-400/70">Labor Budget:</span>
                  <span className="font-semibold text-orange-700 dark:text-orange-300">{formatCurrencyCompact(budget.budget?.labor)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-orange-600/70 dark:text-orange-400/70">Calculated Cost:</span>
                  <span className="font-semibold text-orange-700 dark:text-orange-300">{formatCurrencyCompact(budget.calculatedTeamCost?.totalAnnualCost)}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-orange-600/70 dark:text-orange-400/70">Excess:</span>
                  <span className="font-bold text-orange-700 dark:text-orange-300">
                    {formatCurrencyCompact(Math.abs(budget.variance.laborBudgetVsCalculated))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Budget Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-5 xl:gap-6">
        <MetricCard
          title="Annual Budget"
          value={formatCurrencyCompact(budget?.budget?.annual)}
          subtitle={`FY${formData.budgetFiscalYear}`}
          icon={<Wallet className="w-5 h-5" />}
          iconColor="violet"
        />
        <MetricCard
          title="Committed Cost"
          value={formatCurrencyCompact(budget?.committed?.totalCost ?? 0)}
          subtitle={`${budget?.committed?.committedPercent ?? 0}% of budget`}
          icon={<Target className="w-5 h-5" />}
          iconColor="blue"
        />
        <MetricCard
          title="Total Spent"
          value={formatCurrencyCompact(budget?.actuals?.totalCost)}
          subtitle="Year to date"
          icon={<DollarSign className="w-5 h-5" />}
          iconColor="amber"
        />
        <MetricCard
          title="Remaining"
          value={formatCurrencyCompact(budget?.remaining?.total)}
          subtitle={budget?.remaining?.total && budget.remaining.total < 0 ? 'Over budget' : 'Available'}
          icon={budget?.remaining?.total && budget.remaining.total < 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
          iconColor={budget?.remaining?.total && budget.remaining.total < 0 ? 'rose' : 'emerald'}
        />
        <MetricCard
          title="Utilization"
          value={formatPercent(budget?.utilizationPercent)}
          subtitle={utilizationPercent > 100 ? 'Over budget' : 'On track'}
          icon={<Percent className="w-5 h-5" />}
          iconColor={utilizationColor}
        />
        <MetricCard
          title="Calculated Team Cost"
          value={formatCurrencyCompact(budget?.calculatedTeamCost?.totalAnnualCost ?? 0)}
          subtitle={budget?.calculatedTeamCost ? `${budget.calculatedTeamCost.teamCount} teams, FY${budget.calculatedTeamCost.fiscalYear}` : 'Not calculated'}
          icon={<Calculator className="w-5 h-5" />}
          iconColor="cyan"
        />
      </div>

      {/* Budget Utilization Bar */}
      {budget?.budget?.annual && (
        <GlassCard className="p-6">
          <h4 className="text-sm font-semibold text-[var(--text)] mb-4">Budget Utilization</h4>
          <div className="space-y-4">
            {/* Committed Cost Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[var(--muted)]">Committed (from Allocations)</span>
                <span className="text-sm font-medium text-[var(--text)]">
                  {formatCurrencyCompact(budget.committed?.totalCost ?? 0)} / {formatCurrencyCompact(budget.budget?.annual)}
                </span>
              </div>
              <ProgressBar
                value={Math.min(budget.committed?.committedPercent ?? 0, 100)}
                color={(budget.committed?.committedPercent ?? 0) > 100 ? 'rose' : (budget.committed?.committedPercent ?? 0) > 80 ? 'amber' : 'blue'}
                size="lg"
                gradient
              />
              <div className="flex items-center gap-4 mt-2 text-xs text-[var(--muted)]">
                <span>{budget.committed?.teamWeeks ?? 0} Team Weeks</span>
                <span>{budget.committed?.allocationCount ?? 0} Allocations</span>
                <span>{budget.committed?.epicCount ?? 0} EPICs</span>
              </div>
            </div>

            {/* Actual Spent Progress */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[var(--muted)]">Spent (Actuals YTD)</span>
                <span className="text-sm font-medium text-[var(--text)]">
                  {formatCurrencyCompact(budget.actuals?.totalCost)} / {formatCurrencyCompact(budget.budget?.annual)}
                </span>
              </div>
              <ProgressBar
                value={Math.min(utilizationPercent, 100)}
                color={utilizationColor as 'rose' | 'amber' | 'emerald'}
                size="lg"
                gradient
              />
            </div>

            {budget.budget?.labor && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[var(--muted)]">Labor Budget</span>
                  <span className="text-sm font-medium text-[var(--text)]">
                    {formatCurrencyCompact(budget.actuals?.laborCost)} / {formatCurrencyCompact(budget.budget?.labor)}
                  </span>
                </div>
                <ProgressBar
                  value={budget.budget?.labor > 0 ? Math.min(((budget.actuals?.laborCost ?? 0) / budget.budget.labor) * 100, 100) : 0}
                  color="blue"
                  size="md"
                />
              </div>
            )}

            {budget.budget?.nonLabor && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[var(--muted)]">Non-Labor Budget</span>
                  <span className="text-sm font-medium text-[var(--text)]">
                    {formatCurrencyCompact(budget.actuals?.nonLaborCost)} / {formatCurrencyCompact(budget.budget?.nonLabor)}
                  </span>
                </div>
                <ProgressBar
                  value={budget.budget?.nonLabor > 0 ? Math.min(((budget.actuals?.nonLaborCost ?? 0) / budget.budget.nonLabor) * 100, 100) : 0}
                  color="cyan"
                  size="md"
                />
              </div>
            )}
          </div>
        </GlassCard>
      )}

      {/* Budget Comparison Chart - Budget vs Committed vs Spent */}
      {budget?.budget?.annual && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500/20 to-indigo-600/20">
              <BarChart3 className="w-5 h-5 text-indigo-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text)]">Budget Comparison</h3>
              <p className="text-sm text-[var(--muted)]">Visual comparison of Budget vs Committed vs Spent</p>
            </div>
          </div>

          {/* Horizontal Bar Chart */}
          <div className="space-y-6">
            {/* Chart Legend */}
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-violet-500 to-violet-600"></div>
                <span className="text-[var(--muted)]">Budget</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-blue-500 to-blue-600"></div>
                <span className="text-[var(--muted)]">Committed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-gradient-to-r from-amber-500 to-amber-600"></div>
                <span className="text-[var(--muted)]">Spent</span>
              </div>
            </div>

            {/* Comparative Bar Chart */}
            <div className="relative">
              {/* Budget Bar (100% reference) */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--text)]">Budget</span>
                  <span className="text-sm font-semibold text-violet-600 dark:text-violet-400">
                    {formatCurrencyCompact(budget.budget?.annual)}
                  </span>
                </div>
                <div className="h-8 w-full rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 shadow-sm relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-white">
                    100%
                  </div>
                </div>
              </div>

              {/* Committed Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--text)]">Committed</span>
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                    {formatCurrencyCompact(budget.committed?.totalCost ?? 0)}
                    <span className="text-xs text-[var(--muted)] ml-2">
                      ({formatPercent(budget.committed?.committedPercent ?? 0)})
                    </span>
                  </span>
                </div>
                <div className="h-8 w-full rounded-lg bg-[var(--surface-hover)] relative overflow-hidden">
                  <div
                    className="h-full rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 shadow-sm relative overflow-hidden transition-all duration-500"
                    style={{ width: `${Math.min(budget.committed?.committedPercent ?? 0, 100)}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
                    {(budget.committed?.committedPercent ?? 0) >= 15 && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-white">
                        {formatPercent(budget.committed?.committedPercent ?? 0)}
                      </div>
                    )}
                  </div>
                  {(budget.committed?.committedPercent ?? 0) > 100 && (
                    <div className="absolute -right-2 top-0 h-full flex items-center">
                      <div className="px-2 py-0.5 bg-rose-500 text-white text-xs font-medium rounded-r">
                        Over!
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Spent Bar */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--text)]">Spent</span>
                  <span className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                    {formatCurrencyCompact(budget?.actuals?.totalCost)}
                    <span className="text-xs text-[var(--muted)] ml-2">
                      ({formatPercent(utilizationPercent)})
                    </span>
                  </span>
                </div>
                <div className="h-8 w-full rounded-lg bg-[var(--surface-hover)] relative overflow-hidden">
                  <div
                    className="h-full rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 shadow-sm relative overflow-hidden transition-all duration-500"
                    style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent"></div>
                    {utilizationPercent >= 15 && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-white">
                        {formatPercent(utilizationPercent)}
                      </div>
                    )}
                  </div>
                  {utilizationPercent > 100 && (
                    <div className="absolute -right-2 top-0 h-full flex items-center">
                      <div className="px-2 py-0.5 bg-rose-500 text-white text-xs font-medium rounded-r">
                        Over!
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Summary Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-[var(--border)]">
              <div className="text-center p-3 rounded-lg bg-[var(--surface-hover)]">
                <p className="text-xs text-[var(--muted)] uppercase mb-1">Available Budget</p>
                <p className={`text-base sm:text-lg xl:text-xl font-bold tabular-nums ${(budget.committed?.budgetAfterCommitted ?? budget?.remaining?.total ?? 0) >= 0 ? 'text-status-success' : 'text-status-error'}`}>
                  {formatCurrencyCompact(budget.committed?.budgetAfterCommitted ?? budget?.remaining?.total)}
                </p>
                <p className="text-xs text-[var(--muted)] mt-1">After committed</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[var(--surface-hover)]">
                <p className="text-xs text-[var(--muted)] uppercase mb-1">Committed vs Spent</p>
                <p className={`text-base sm:text-lg xl:text-xl font-bold tabular-nums ${(budget.committed?.totalCost ?? 0) - (budget?.actuals?.totalCost ?? 0) >= 0 ? 'text-accent-blue' : 'text-status-warning'}`}>
                  {formatCurrencyCompact((budget.committed?.totalCost ?? 0) - (budget?.actuals?.totalCost ?? 0))}
                </p>
                <p className="text-xs text-[var(--muted)] mt-1">
                  {(budget.committed?.totalCost ?? 0) >= (budget?.actuals?.totalCost ?? 0) ? 'Still to spend' : 'Overspent'}
                </p>
              </div>
              <div className="text-center p-3 rounded-lg bg-[var(--surface-hover)]">
                <p className="text-xs text-[var(--muted)] uppercase mb-1">Budget Health</p>
                <p className={`text-base sm:text-lg xl:text-xl font-bold ${
                  utilizationPercent > 100 ? 'text-status-error' :
                  utilizationPercent > 80 ? 'text-status-warning' :
                  'text-status-success'
                }`}>
                  {utilizationPercent > 100 ? 'Over Budget' :
                   utilizationPercent > 80 ? 'At Risk' :
                   'On Track'}
                </p>
                <p className="text-xs text-[var(--muted)] mt-1">
                  {formatPercent(100 - utilizationPercent)} remaining
                </p>
              </div>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Labor Budget vs Calculated Team Cost Comparison */}
      {budget?.calculatedTeamCost && budget?.budget?.labor && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-cyan-600/20">
              <Calculator className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text)]">Labor Budget vs Calculated Team Cost</h3>
              <p className="text-sm text-[var(--muted)]">Variance analysis comparing Finance budget to actual team costs</p>
            </div>
          </div>

          {/* Side-by-side comparison */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-gradient-to-br from-blue-50/80 to-blue-100/40 dark:from-blue-900/20 dark:to-blue-800/10 rounded-xl text-center">
              <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-1">Labor Budget (Manual)</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-blue-600 dark:text-blue-400">
                {formatCurrencyCompact(budget.budget.labor)}
              </p>
              <p className="text-xs text-[var(--muted)] mt-1">Set by Finance</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-cyan-50/80 to-cyan-100/40 dark:from-cyan-900/20 dark:to-cyan-800/10 rounded-xl text-center">
              <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-1">Calculated Team Cost</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-cyan-600 dark:text-cyan-400">
                {formatCurrencyCompact(budget.calculatedTeamCost.totalAnnualCost)}
              </p>
              <p className="text-xs text-[var(--muted)] mt-1">{budget.calculatedTeamCost.teamCount} teams, FY{budget.calculatedTeamCost.fiscalYear}</p>
            </div>
            <div className={`p-4 rounded-xl text-center ${
              budget.variance?.status === 'OVER_BUDGET'
                ? 'bg-status-error-bg'
                : budget.variance?.status === 'UNDER_BUDGET'
                  ? 'bg-status-success-bg'
                  : 'bg-[var(--surface-muted)]'
            }`}>
              <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-1">Variance</p>
              <p className={`text-lg sm:text-xl xl:text-2xl font-bold tabular-nums ${
                budget.variance?.status === 'OVER_BUDGET'
                  ? 'text-status-error'
                  : budget.variance?.status === 'UNDER_BUDGET'
                    ? 'text-status-success'
                    : 'text-muted-foreground'
              }`}>
                {budget.variance ? formatCurrencyCompact(budget.variance.laborBudgetVsCalculated) : 'N/A'}
              </p>
              <p className={`text-xs mt-1 ${
                budget.variance?.status === 'OVER_BUDGET'
                  ? 'text-status-error'
                  : budget.variance?.status === 'UNDER_BUDGET'
                    ? 'text-status-success'
                    : 'text-[var(--muted)]'
              }`}>
                {budget.variance?.status === 'OVER_BUDGET' ? 'Over Budget' :
                 budget.variance?.status === 'UNDER_BUDGET' ? 'Under Budget' : 'On Budget'}
                {budget.variance && ` (${formatPercent(Math.abs(budget.variance.laborBudgetVsCalculatedPercent))})`}
              </p>
            </div>
          </div>

          {/* Progress bar showing calculated vs budget */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[var(--muted)]">Calculated Team Cost vs Labor Budget</span>
              <span className="text-sm font-medium text-[var(--text)]">
                {formatCurrencyCompact(budget.calculatedTeamCost.totalAnnualCost)} / {formatCurrencyCompact(budget.budget.labor)}
              </span>
            </div>
            <ProgressBar
              value={Math.min(((budget.calculatedTeamCost.totalAnnualCost / budget.budget.labor) * 100), 100)}
              color={budget.variance?.status === 'OVER_BUDGET' ? 'rose' : budget.variance?.status === 'UNDER_BUDGET' ? 'emerald' : 'cyan'}
              size="lg"
              gradient
            />
            <div className="flex items-center justify-between mt-2 text-xs text-[var(--muted)]">
              <span>{formatPercent((budget.calculatedTeamCost.totalAnnualCost / budget.budget.labor) * 100)} of labor budget</span>
              {budget.variance?.status === 'OVER_BUDGET' && (
                <span className="text-status-error font-medium">
                  Exceeds budget by {formatCurrencyCompact(Math.abs(budget.variance.laborBudgetVsCalculated))}
                </span>
              )}
            </div>
          </div>

          {/* Team breakdown table (collapsible) */}
          {budget.calculatedTeamCost.teamBreakdown && budget.calculatedTeamCost.teamBreakdown.length > 0 && (
            <div className="border-t border-[var(--border)] pt-4">
              <button
                onClick={() => setShowTeamBreakdown(!showTeamBreakdown)}
                className="flex items-center gap-2 text-sm font-medium text-[var(--text)] hover:text-accent-blue transition-colors"
              >
                {showTeamBreakdown ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                View team breakdown ({budget.calculatedTeamCost.teamBreakdown.length} teams)
              </button>

              {showTeamBreakdown && (
                <div className="mt-4 overflow-x-auto -mx-4 px-4">
                  {/* Premium Glass-Morphism Cost Comparison Table */}
                  <div className="rounded-xl overflow-hidden border border-[var(--border)] bg-white/40 dark:bg-white/5 backdrop-blur-sm shadow-sm">
                    <table className="w-full text-sm min-w-[700px]">
                      <thead>
                        <tr className="bg-gradient-to-r from-slate-100/80 to-slate-50/80 dark:from-slate-800/60 dark:to-slate-700/40">
                          <th className="text-left py-3 px-4 font-semibold text-[var(--muted)] uppercase text-xs tracking-wider min-w-[140px]">Team</th>
                          <th className="text-center py-3 px-2 font-semibold text-[var(--muted)] uppercase text-xs tracking-wider w-16">Members</th>
                          <th className="text-left py-3 px-3 font-semibold text-[var(--muted)] uppercase text-xs tracking-wider w-[90px]">Profile</th>
                          <th className="text-right py-3 px-3 font-semibold text-[var(--muted)] uppercase text-xs tracking-wider w-[110px]">Planned</th>
                          <th className="text-right py-3 px-3 font-semibold text-[var(--muted)] uppercase text-xs tracking-wider w-[110px]">Calculated</th>
                          <th className="text-right py-3 px-3 font-semibold text-[var(--muted)] uppercase text-xs tracking-wider w-[120px]">Variance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[var(--border)]/50">
                        {budget.calculatedTeamCost.teamBreakdown.map((team, index) => {
                          const variance = team.annualCost - (team.plannedCost || 0);
                          const variancePercent = team.plannedCost ? ((variance / team.plannedCost) * 100) : 0;
                          const isOver = variance > 0;
                          return (
                            <tr
                              key={team.teamId}
                              className={`transition-colors hover:bg-white/60 dark:hover:bg-white/10 ${
                                index % 2 === 0 ? 'bg-white/20 dark:bg-white/[0.02]' : 'bg-transparent'
                              }`}
                            >
                              <td className="py-2.5 px-4 text-[var(--text)] font-medium">{team.teamName}</td>
                              <td className="py-2.5 px-2 text-center text-[var(--text)] tabular-nums">{team.memberCount}</td>
                              <td className="py-2.5 px-3 text-[var(--muted)] text-xs">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300">
                                  {team.costProfileName || 'Standard'}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-right font-medium text-blue-600 dark:text-blue-400 tabular-nums">
                                {formatCurrencyCompact(team.plannedCost || 0)}
                              </td>
                              <td className="py-2.5 px-3 text-right font-medium text-cyan-600 dark:text-cyan-400 tabular-nums">
                                {formatCurrencyCompact(team.annualCost)}
                              </td>
                              <td className={`py-2.5 px-3 text-right font-medium tabular-nums ${
                                isOver ? 'text-orange-600 dark:text-orange-400' : 'text-emerald-600 dark:text-emerald-400'
                              }`}>
                                <span className="inline-flex items-center gap-1">
                                  {isOver ? (
                                    <TrendingUp className="w-3.5 h-3.5" />
                                  ) : (
                                    <TrendingDown className="w-3.5 h-3.5" />
                                  )}
                                  {isOver ? '+' : ''}{formatCurrencyCompact(variance)}
                                  <span className="text-xs opacity-70">({variancePercent.toFixed(0)}%)</span>
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gradient-to-r from-slate-200/80 to-slate-100/80 dark:from-slate-700/60 dark:to-slate-600/40">
                          <td className="py-3 px-4 font-bold text-[var(--text)] uppercase text-xs tracking-wide">Total</td>
                          <td className="py-3 px-2 text-center font-bold text-[var(--text)] tabular-nums">
                            {budget.calculatedTeamCost.teamBreakdown.reduce((sum, t) => sum + t.memberCount, 0)}
                          </td>
                          <td className="py-3 px-3 text-[var(--muted)] text-xs">
                            {budget.calculatedTeamCost.teamBreakdown.length} teams
                          </td>
                          <td className="py-3 px-3 text-right font-bold text-blue-700 dark:text-blue-300 tabular-nums">
                            {formatCurrencyCompact(budget.calculatedTeamCost.totalPlannedCost || 0)}
                          </td>
                          <td className="py-3 px-3 text-right font-bold text-cyan-700 dark:text-cyan-300 tabular-nums">
                            {formatCurrencyCompact(budget.calculatedTeamCost.totalAnnualCost)}
                          </td>
                          {(() => {
                            const totalVariance = budget.calculatedTeamCost.totalAnnualCost - (budget.calculatedTeamCost.totalPlannedCost || 0);
                            const totalVariancePercent = budget.calculatedTeamCost.totalPlannedCost
                              ? ((totalVariance / budget.calculatedTeamCost.totalPlannedCost) * 100)
                              : 0;
                            const isOverTotal = totalVariance > 0;
                            return (
                              <td className={`py-3 px-3 text-right font-bold tabular-nums ${
                                isOverTotal ? 'text-orange-700 dark:text-orange-300' : 'text-emerald-700 dark:text-emerald-300'
                              }`}>
                                <span className="inline-flex items-center gap-1">
                                  {isOverTotal ? (
                                    <TrendingUp className="w-4 h-4" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4" />
                                  )}
                                  {isOverTotal ? '+' : ''}{formatCurrencyCompact(totalVariance)}
                                  <span className="text-xs opacity-70">({totalVariancePercent.toFixed(0)}%)</span>
                                </span>
                              </td>
                            );
                          })()}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No team data warning */}
          {(!budget.calculatedTeamCost.teamBreakdown || budget.calculatedTeamCost.teamBreakdown.length === 0) && (
            <div className="p-4 bg-status-warning-bg border border-status-warning/30 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-status-warning" />
                <p className="text-sm text-status-warning">
                  No active teams found. Team cost calculation requires teams with active resource assignments and rate cards.
                </p>
              </div>
            </div>
          )}
        </GlassCard>
      )}

      {/* No labor budget set message */}
      {budget?.calculatedTeamCost && !budget?.budget?.labor && (
        <GlassCard className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500/20 to-cyan-600/20">
              <Calculator className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text)]">Calculated Team Cost</h3>
              <p className="text-sm text-[var(--muted)]">Based on resource assignments and rate cards</p>
            </div>
          </div>
          <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
            <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400 mb-2">
              {formatCurrencyCompact(budget.calculatedTeamCost.totalAnnualCost)}
            </p>
            <p className="text-sm text-[var(--muted)]">
              {budget.calculatedTeamCost.teamCount} teams • FY{budget.calculatedTeamCost.fiscalYear}
            </p>
            <p className="text-sm text-status-warning mt-2">
              Set a labor budget above to see variance analysis.
            </p>
          </div>
        </GlassCard>
      )}

      {/* Budget Configuration Form */}
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500/20 to-violet-600/20">
              <PiggyBank className="w-5 h-5 text-violet-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--text)]">Budget Configuration</h3>
              <p className="text-sm text-[var(--muted)]">Configure annual budget for {programName}</p>
            </div>
          </div>
          {canEdit && isDirty && (
            <GlassButton
              variant="primary"
              size="sm"
              onClick={handleSave}
              isLoading={loading.saving}
              leftIcon={<Save className="w-4 h-4" />}
            >
              Save Changes
            </GlassButton>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
          {/* Fiscal Year */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Fiscal Year
            </label>
            <select
              value={formData.budgetFiscalYear}
              onChange={(e) => handleInputChange('budgetFiscalYear', e.target.value)}
              disabled={!canEdit}
              className="w-full px-4 py-2.5 bg-white/60 dark:bg-white/10 border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {[2024, 2025, 2026, 2027, 2028].map((year) => (
                <option key={year} value={year}>
                  FY{year}
                </option>
              ))}
            </select>
          </div>

          {/* Annual Budget */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Annual Budget
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]">$</span>
              <input
                type="number"
                value={formData.annualBudget}
                onChange={(e) => handleInputChange('annualBudget', e.target.value)}
                disabled={!canEdit}
                placeholder="5,000,000"
                className="w-full pl-8 pr-4 py-2.5 bg-white/60 dark:bg-white/10 border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Operational Overhead */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Operational Overhead
            </label>
            <div className="relative">
              <input
                type="number"
                value={formData.operationalOverhead}
                onChange={(e) => handleInputChange('operationalOverhead', e.target.value)}
                disabled={!canEdit}
                placeholder="20"
                min="0"
                max="100"
                className="w-full pr-8 pl-4 py-2.5 bg-white/60 dark:bg-white/10 border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--muted)]">%</span>
            </div>
            <p className="mt-1 text-xs text-[var(--muted)]">Percentage of budget for operations</p>
          </div>

          {/* Labor Budget */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Labor Budget
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]">$</span>
              <input
                type="number"
                value={formData.laborBudget}
                onChange={(e) => handleInputChange('laborBudget', e.target.value)}
                disabled={!canEdit}
                placeholder="4,000,000"
                className="w-full pl-8 pr-4 py-2.5 bg-white/60 dark:bg-white/10 border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Non-Labor Budget */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Non-Labor Budget
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]">$</span>
              <input
                type="number"
                value={formData.nonLaborBudget}
                onChange={(e) => handleInputChange('nonLaborBudget', e.target.value)}
                disabled={!canEdit}
                placeholder="1,000,000"
                className="w-full pl-8 pr-4 py-2.5 bg-white/60 dark:bg-white/10 border border-[var(--border)] rounded-xl text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-accent-blue/50 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <p className="mt-1 text-xs text-[var(--muted)]">Tools, licenses, infrastructure, etc.</p>
          </div>
        </div>

        {!canEdit && (
          <div className="mt-4 p-3 bg-status-warning-bg border border-status-warning/30 rounded-lg">
            <p className="text-sm text-status-warning">
              You do not have permission to edit budget settings. Contact an administrator or finance team member.
            </p>
          </div>
        )}
      </GlassCard>

      {/* Committed vs Spent Summary */}
      {budget && (
        <GlassCard className="p-6">
          <h4 className="text-sm font-semibold text-[var(--text)] mb-4">Budget Status</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50/80 to-blue-100/40 dark:from-blue-900/20 dark:to-blue-800/10 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-blue-500" />
                <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Committed</p>
              </div>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-blue-600 dark:text-blue-400">
                {formatCurrencyCompact(budget.committed?.totalCost ?? 0)}
              </p>
              <p className="text-xs text-[var(--muted)] mt-1">{budget.committed?.teamWeeks ?? 0} Team Weeks</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-amber-50/80 to-amber-100/40 dark:from-amber-900/20 dark:to-amber-800/10 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-amber-500" />
                <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Spent</p>
              </div>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
                {formatCurrencyCompact(budget?.actuals?.totalCost)}
              </p>
              <p className="text-xs text-[var(--muted)] mt-1">Labor: {formatCurrencyCompact(budget?.actuals?.laborCost)}</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-emerald-50/80 to-emerald-100/40 dark:from-emerald-900/20 dark:to-emerald-800/10 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-emerald-500" />
                <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Available</p>
              </div>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                {formatCurrencyCompact(budget.committed?.budgetAfterCommitted ?? budget?.remaining?.total)}
              </p>
              <p className="text-xs text-[var(--muted)] mt-1">After committed</p>
            </div>
            <div className="p-4 bg-gradient-to-br from-violet-50/80 to-violet-100/40 dark:from-violet-900/20 dark:to-violet-800/10 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-violet-500" />
                <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide">Allocations</p>
              </div>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-violet-600 dark:text-violet-400">
                {budget.committed?.allocationCount ?? 0}
              </p>
              <p className="text-xs text-[var(--muted)] mt-1">{budget.committed?.epicCount ?? 0} EPICs</p>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Cost Breakdown Summary */}
      {budget?.actuals && (
        <GlassCard className="p-6">
          <h4 className="text-sm font-semibold text-[var(--text)] mb-4">Actuals Breakdown</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-gradient-to-br from-blue-50/80 to-blue-100/40 dark:from-blue-900/20 dark:to-blue-800/10 rounded-xl">
              <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-1">Labor Costs</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-blue-600 dark:text-blue-400">
                {formatCurrencyCompact(budget?.actuals?.laborCost)}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-cyan-50/80 to-cyan-100/40 dark:from-cyan-900/20 dark:to-cyan-800/10 rounded-xl">
              <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-1">Non-Labor Costs</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-cyan-600 dark:text-cyan-400">
                {formatCurrencyCompact(budget?.actuals?.nonLaborCost)}
              </p>
            </div>
            <div className="p-4 bg-gradient-to-br from-violet-50/80 to-violet-100/40 dark:from-violet-900/20 dark:to-violet-800/10 rounded-xl">
              <p className="text-xs font-medium text-[var(--muted)] uppercase tracking-wide mb-1">Total Costs</p>
              <p className="text-lg sm:text-xl xl:text-2xl font-bold tabular-nums text-violet-600 dark:text-violet-400">
                {formatCurrencyCompact(budget?.actuals?.totalCost)}
              </p>
            </div>
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default ProgramBudgetTab;
